import React, { useState } from "react";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Share2, Copy, Facebook, Instagram, MessageCircle, ImageDown } from "lucide-react";

export const shareUrl = (pid) => `${API}/shop/share${pid ? `/${pid}` : ""}`;

const copy = async (url, msg = "Link copied") => {
  try { await navigator.clipboard.writeText(url); toast.success(msg); } catch { window.prompt("Copy this link", url); }
};

const Opt = ({ icon: Icon, label, onClick, testid, tone }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center gap-3 w-full rounded-xl border border-[#D4AF37]/25 bg-[#0B0F0D]/60 px-4 h-12 text-sm font-semibold text-[#F5EFE6] hover:border-[#D4AF37]/70 hover:bg-[#0D382B] transition-colors`}>
    <span className={`w-8 h-8 rounded-full flex items-center justify-center ${tone}`}><Icon className="w-4 h-4 text-white" /></span>{label}
  </button>
);

export const ShareMenu = ({ open, onClose, pid, title }) => {
  const url = shareUrl(pid);
  const text = title ? `${title} — Bengal Umrah House VIP Shop` : "Bengal Umrah House VIP Shop — sacred Umrah essentials";
  const native = async () => {
    try { await navigator.share({ title: text, text, url }); } catch { /* cancelled */ }
  };
  const postImage = async () => {
    const src = `${API}/shop/post-image/${pid}.png`;
    try {
      const blob = await (await fetch(src)).blob();
      const file = new File([blob], "bengal-umrah-house-post.jpg", { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], text: `${text}\n${url}` }); return; }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
      toast.success("Square post image downloaded — upload it to Instagram and add the link in your bio or story");
    } catch { window.open(src, "_blank", "noopener"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-[#05261D] border-[#D4AF37]/30 rounded-2xl" data-testid="share-menu">
        <DialogHeader><DialogTitle className="font-serif text-[#FFE9A8] flex items-center gap-2"><Share2 className="w-5 h-5" /> Share {pid ? "this product" : "the VIP Shop"}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {pid && <Opt icon={ImageDown} label="Instagram post image (square)" onClick={postImage} testid="share-post-image-btn" tone="bg-[linear-gradient(45deg,#D4AF37,#8134AF)]" />}
          {navigator.share && <Opt icon={Share2} label="Share to apps (Instagram, Facebook…)" onClick={native} testid="share-native-btn" tone="bg-[#156B52]" />}
          <Opt icon={Facebook} label="Share on Facebook" testid="share-facebook-btn" tone="bg-[#1877F2]"
            onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener,width=640,height=560")} />
          <Opt icon={Instagram} label="Instagram (copy link for bio / story)" testid="share-instagram-btn" tone="bg-[linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)]"
            onClick={() => copy(url, "Link copied — paste it in your Instagram bio, story link sticker or post caption")} />
          <Opt icon={MessageCircle} label="Share on WhatsApp" testid="share-whatsapp-btn" tone="bg-[#25D366]"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank", "noopener")} />
          <Opt icon={Copy} label="Copy link" onClick={() => copy(url)} testid="share-copy-btn" tone="bg-[#D4AF37]/80" />
        </div>
        <p className="text-[10px] text-[#A3B8B0] break-all" data-testid="share-url">{url}</p>
      </DialogContent>
    </Dialog>
  );
};
